import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";

import { ButtonPrimary, ButtonTertiary } from "components/Button";

import useWindowDimensions from "hooks/useWindowDimensions";

const Modal = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  flex-direction: column;
  background-color: ${(p) => p.theme.colors.green};
  opacity: 0;
  visibility: hidden;
  z-index: 1000;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  padding: 0 ${(p) => p.theme.grid.containerPadding.mobile}px;
  height: 70px;
  border-bottom: solid 1px ${(p) => p.theme.colors.ltGreen};
`;

const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 0 0 75px;
  max-width: 75px;
`;

const HeaderLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  flex: 0 0 75px;
  max-width: 75px;
`;

const Title = styled.div`
  font-size: 14px;
`;

const Subtitle = styled.div`
  margin: 5px 0 0 0;
  font-size: 10px;
`;

const Close = styled.button`
  position: relative;
  right: -10px;
  padding: 10px;
  i {
    font-size: 24px;
    color: ${(p) => p.theme.colors.ltGreen};
  }
`;

const Back = styled.button`
  position: relative;
  left: -10px;
  padding: 10px;
  i {
    font-size: 16px;
    color: ${(p) => p.theme.colors.white};
    opacity: 0.55;
  }
`;

const Pages = styled.div`
  flex: 1;
  overflow: hidden;
`;

const PagesTrack = styled.div`
  display: flex;
  height: 100%;
`;

const Page = styled.div`
  position: relative;
  flex: 1 0 100%;
  height: 100%;
  overflow-y: scroll;
  --webkit-overflow-scrolling: touch;
`;

const Toolbar = styled.div`
  display: flex;
  margin: 30px 0 50px 0;
  padding: 0 ${(p) => p.theme.grid.containerPadding.mobile}px;
  button {
    flex: 1;
    margin: 0 15px 0 0;
    &:last-child {
      margin: 0;
    }
  }
`;

const NavigationItemCheck = styled.div`
  color: ${(p) => p.theme.colors.cream};
  font-size: 12px;
`;

const NavigationItemEl = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  padding: 0 ${p => p.theme.grid.containerPadding.mobile}px;
  border: ${p => p.groupName === "Color" ? `solid 1px ${p.theme.colors.ltGreen}` : "none"};
  border-bottom: solid 1px ${p => p.theme.colors.ltGreen};
  background-color: ${p => p.active ? p.theme.colors.ltGreen : 'transparent'};
  cursor: pointer;
  & ${NavigationItemCheck} {
    display: ${(p) => (p.active ? "block" : "none")};
  }
`;

const NavigationItemTitle = styled.div`
  ${(p) => p.theme.typography.tMedium};
`;

const NavigationItemNext = styled.div`
  color: ${(p) => p.theme.colors.cream};
  font-size: 12px;
`;

export const NavigationItem = ({
  title,
  active = false,
  type = "select",
  onClick,
  groupName
}) => {  
  const renderAdornment = () => {
    switch (type) {
      case "page":
        return (
          <NavigationItemNext>
            <i className="icon-chevron-right"></i>
          </NavigationItemNext>
        );
      case "check":
        return (
          <NavigationItemCheck>
            <i className="icon-close"></i>
          </NavigationItemCheck>
        );
      default:
        return;
    }
  };

  return (
    <NavigationItemEl onClick={onClick} active={active} groupName={groupName}>
      <NavigationItemTitle>{title}</NavigationItemTitle>
      {renderAdornment()}
    </NavigationItemEl>
  );
};

const PagedModal = ({
  title,
  children,
  confirmable,
  confirmText,
  onConfirm,
  cancelable,
  cancelText,
  onCancel,
  active,
  onCloseClick,
  page,
  setPage,
}) => {
  const { height } = useWindowDimensions();
  const modalRef = useRef();
  const pagesTrack = useRef();
  const [titles, setTitles] = useState([title]);

  useEffect(() => {
    modalRef.current.style.height = `${height}px`;
  }, [height]);

  useEffect(() => {
    if (active) {
      gsap.fromTo(
        modalRef.current,
        0.5,
        {
          autoAlpha: 0,
          y: 15,
        },
        {
          autoAlpha: 1,
          y: 0,
          ease: "power4.out",
        }
      );
    } else {
      gsap.to(modalRef.current, 0.25, {
        autoAlpha: 0,
        ease: "power4.out",
      });
    }
  }, [active]);

  useEffect(() => {
    gsap.to(pagesTrack.current, 0.6, {
      x: `-${(page - 1) * 100}%`,
      ease: "power4.inOut",
    });
  }, [page]);

  const onPageItemClick = ({ title, to }) => {
    setTitles([...titles, title]);
    setPage(to);
  };

  const onBackClick = () => {
    setTitles(titles.filter((t, i) => i !== titles.length - 1));
    setPage(page - 1);
  };

  const renderTitle = () => {
    let title = titles[titles.length - 1];

    if (title) return <Title>{title}</Title>;
  };

  const renderSubtitle = () => {
    const currentPage = React.Children.toArray(children)[page - 1];
    let selected = 0;

    React.Children.map(currentPage?.props?.children, (child) => {
      if (!child) return;
      if (child.props.type === "check" && child.props.active) {
        selected++;
      }
    });

    if (selected > 0) return <Subtitle>{selected} Selected</Subtitle>;
  };

  const renderBack = () => {
    if (page === 1) return;

    return (
      <Back onClick={onBackClick}>
        <i className="icon-chevron-left"></i>
      </Back>
    );
  };

  const renderClose = () => {
    return (
      <Close onClick={onCloseClick}>
        <i className="icon-close"></i>
      </Close>
    );
  };

  const renderCancel = () => {
    if (cancelable)
      return (
        <ButtonTertiary onClick={onCancel}>
          {cancelText || "Cancel"}
        </ButtonTertiary>
      );
  };

  const renderConfirm = () => {
    if (confirmable)
      return (
        <ButtonPrimary onClick={onConfirm}>{confirmText || "Ok"}</ButtonPrimary>
      );
  };

  return (
    <Modal ref={modalRef}>
      <Header>
        <HeaderLeft>{renderBack()}</HeaderLeft>
        <HeaderCenter>
          {renderTitle()}
          {renderSubtitle()}
        </HeaderCenter>
        <HeaderRight>{renderClose()}</HeaderRight>
      </Header>
      <Pages>
        <PagesTrack ref={pagesTrack}>
          {React.Children.map(children, (child) => (
            <Page>
              {React.Children.map(child?.props?.children, (child) => {
                if (!child) return;

                switch (child.props.type) {
                  case "page":
                    return React.cloneElement(child, {
                      onClick: (e) => {
                        if (child.props.onClick)
                          child.props.onClick(e, child.props);
                        onPageItemClick(child.props);
                      },
                    });
                  default:
                    return child;
                }
              })}
            </Page>
          ))}
        </PagesTrack>
      </Pages>
      <Toolbar>
        {renderCancel()}
        {renderConfirm()}
      </Toolbar>
    </Modal>
  );
};

export default PagedModal;